import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { RestaurantTablesService } from '../restaurant-tables.service';
import { DiningArea, DiningAreasService } from '../../dining-areas/dining-areas.service';

export interface TableFormDialogData {
  id?: string;
  diningAreaId?: string;
}

@Component({
  selector: 'app-table-form-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>
        <mat-icon>table_bar</mat-icon>
        {{ isEditMode ? 'Edit Table' : 'Create Table' }}
      </h2>
      <button mat-icon-button type="button" (click)="onCancel()" [disabled]="loading">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <form [formGroup]="form" (ngSubmit)="onSubmit()" id="tableForm">
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Table number</mat-label>
            <input matInput formControlName="table_number" placeholder="e.g. T1" required />
            <mat-error>{{ getError('table_number') }}</mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Capacity</mat-label>
            <input matInput type="number" formControlName="capacity" min="0" />
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Dining area</mat-label>
          <mat-select formControlName="dining_area_id">
            <mat-option [value]="null">— None —</mat-option>
            <mat-option *ngFor="let area of areas" [value]="area.id">{{ area.name }}</mat-option>
          </mat-select>
          <mat-hint *ngIf="lockArea">Set from the selected dining area section.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Status</mat-label>
          <mat-select formControlName="status">
            <mat-option value="available">Available</mat-option>
            <mat-option value="occupied">Occupied</mat-option>
            <mat-option value="reserved">Reserved</mat-option>
            <mat-option value="out_of_service">Out of service</mat-option>
          </mat-select>
        </mat-form-field>

        <div class="form-section">
          <mat-slide-toggle formControlName="is_active" color="accent">Active</mat-slide-toggle>
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()" [disabled]="loading">Cancel</button>
      <button mat-raised-button color="primary" form="tableForm" type="submit" [disabled]="loading || form.invalid">
        <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
        <span *ngIf="!loading">{{ isEditMode ? 'Update' : 'Create' }}</span>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-header { display: flex; align-items: center; justify-content: space-between; }
    .dialog-header h2 { display: flex; align-items: center; gap: 8px; margin: 0; }
    mat-dialog-content { min-width: 520px; padding-top: 8px; }
    .full-width { width: 100%; }
    .form-row { display: flex; gap: 16px; }
    .form-row mat-form-field { flex: 1; }
    .form-section { margin: 8px 0; }
    mat-dialog-actions button mat-spinner { display: inline-block; margin-right: 8px; }
    @media (max-width: 600px) { mat-dialog-content { min-width: unset; } .form-row { flex-direction: column; gap: 0; } }
  `],
})
export class TableFormDialogComponent implements OnInit {
  form: FormGroup;
  loading = false;
  isEditMode: boolean;
  lockArea: boolean;
  id?: string;
  areas: DiningArea[] = [];

  constructor(
    private fb: FormBuilder,
    private service: RestaurantTablesService,
    private areasService: DiningAreasService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<TableFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TableFormDialogData,
  ) {
    this.id = data?.id;
    this.isEditMode = !!this.id;
    this.lockArea = !this.isEditMode && !!data?.diningAreaId;
    this.form = this.fb.group({
      table_number: ['', [Validators.required, Validators.maxLength(50)]],
      capacity: [0, [Validators.min(0)]],
      dining_area_id: [{ value: data?.diningAreaId ?? null, disabled: this.lockArea }],
      status: ['available', Validators.required],
      is_active: [true],
    });
  }

  ngOnInit(): void {
    this.areasService.getAll().subscribe({
      next: (res) => {
        if (res.success && res.data) this.areas = res.data;
        if (this.isEditMode) this.load();
      },
      error: () => {
        if (this.isEditMode) this.load();
      },
    });
  }

  private load(): void {
    if (!this.id) return;
    this.loading = true;
    this.service.get(this.id).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.form.patchValue({
            table_number: res.data.table_number,
            capacity: res.data.capacity,
            dining_area_id: res.data.dining_area_id ?? null,
            status: res.data.status,
            is_active: res.data.is_active,
          });
        }
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load table', 'Close', { duration: 3000 });
        this.loading = false;
        this.dialogRef.close(false);
      },
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    const payload = this.form.getRawValue();
    const request$ = this.isEditMode && this.id
      ? this.service.update(this.id, payload)
      : this.service.create(payload);

    request$.subscribe({
      next: (res) => {
        if (res.success) {
          this.snackBar.open(`Table ${this.isEditMode ? 'updated' : 'created'} successfully`, 'Close', { duration: 3000 });
          this.dialogRef.close(true);
        }
        this.loading = false;
      },
      error: (error) => {
        this.snackBar.open(error.error?.message || 'Failed to save table', 'Close', { duration: 5000 });
        if (error.error?.errors) {
          Object.keys(error.error.errors).forEach((key) => {
            this.form.get(key)?.setErrors({ serverError: error.error.errors[key][0] });
          });
        }
        this.loading = false;
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getError(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control?.errors) return '';
    if (control.errors['required']) return 'This field is required';
    if (control.errors['maxlength']) return 'Too long';
    if (control.errors['serverError']) return control.errors['serverError'];
    return 'Invalid value';
  }
}
